import {
  Box,
  BoxProps,
  Input,
  InputProps,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure,
  useUpdateEffect
} from '@chakra-ui/react'
import React, { useMemo, useRef, useState } from 'react'
import MultiRef from 'react-multi-ref'
import scrollIntoView from 'scroll-into-view-if-needed'
import { accountItem, reportCategory } from 'src/api'
import { FILTER_ACCOUNT_ITEM } from 'src/constants'
import { usePartnerTaxAccountantCategories } from 'src/hooks/tax_accountant/usePartnerTaxAccountantCategories'
import { useReportCategories } from 'src/hooks/useReportCategories'
import { useAdminUltraReportCategories } from 'src/hooks/zo_admin/ultra/useAdminUltraReportCategories'
import { SelectTriangleIcon } from '../Icons/SelectTriangleIcon'

export type JournalCategoryType = 'income' | 'payment'

const AccountItemGroupHeader: React.FC<BoxProps> = ({ children, ...props }) => {
  return (
    <Box
      fontSize="14px"
      lineHeight="1"
      padding="12px"
      margin="0"
      color="white.100"
      fontWeight={600}
      {...props}
    >
      {children}
    </Box>
  )
}

const AccountItemBox = React.forwardRef<
  HTMLDivElement,
  BoxProps & { onClick: VoidFunction }
>(({ children, onClick, ...props }, ref) => {
  return (
    <Box
      ref={ref}
      onClick={onClick}
      fontSize="13px"
      paddingY="15px"
      paddingX="22px"
      lineHeight="1"
      borderLeft="2px"
      borderBottom="1px"
      borderBottomColor="black.12"
      cursor="pointer"
      _hover={{
        bgColor: 'gray.100'
      }}
      {...props}
    >
      {children}
    </Box>
  )
})

type Props = Omit<InputProps, 'onChange'> & {
  onChange: (category: accountItem | null) => void
  selectedAccountItemId?: accountItem['id'] | null
  journalCategoryType: JournalCategoryType
  editMode: 'easy' | 'pro'
  /**
   * 管理者用画面でしか使わない
   */
  hasUnselected?: boolean
  /**
   * Popoverのトリガー（ボタンとして表示するUI）を設定できる
   */
  popoverTrigger?: JSX.Element
  /**
   * Modalの上でCategorySelectBoxを表示する際に渡すref
   * これを渡さないとzIndexがずれる
   */
  containerRef?: React.RefObject<HTMLDivElement>
}

const BaseCategorySelectBox: React.FC<
  Props & { reportCategories: reportCategory[] }
> = ({
  onChange,
  selectedAccountItemId,
  journalCategoryType,
  editMode,
  hasUnselected,
  reportCategories,
  popoverTrigger,
  containerRef,
  ...props
}) => {
  const [inputText, setInputText] = useState('')
  const { onClose, onOpen, isOpen } = useDisclosure()
  const [menuNodes] = React.useState(() => new MultiRef<number, HTMLElement>())
  const menuRef = React.useRef<HTMLDivElement>(null)
  const firstFouceRef = useRef<HTMLInputElement>(null)
  const [focusedIndex, setFocusedIndex] = useState(0)

  // 0番目: その他(0)
  // 1番目: 収益(4)
  // 2番目: 負債(2)
  // 3番目: 費用(5)
  // 4番目: 資産(1)
  // 5(非表示): 純資産(3)、の順に並べたい
  // 勘定科目の親分類の数字に+1したindexの要素にその分類の順番を持つ。
  const sortOrder =
    journalCategoryType === 'income' ? [0, 4, 2, 5, 1, 3] : [0, 2, 4, 5, 3, 1]

  const filteredReportCategories = useMemo(
    () =>
      [
        // 管理者用画面でしか使わない
        ...(hasUnselected
          ? [
              {
                category_type: -9999 as reportCategory.category_type,
                account_items: [
                  {
                    name: '未選択',
                    id: -9999
                  } as accountItem
                ]
              }
            ]
          : []),
        ...reportCategories,
        ...(editMode === 'easy'
          ? [
              {
                category_type: -1 as reportCategory.category_type,
                account_items: [
                  {
                    name: 'プライベート',
                    id: -1
                  } as accountItem
                ]
              }
            ]
          : [])
      ]
        ?.filter((v) => v.category_type !== 2) //純資産を除外
        .sort(
          (a, b) =>
            sortOrder[(a.category_type || 0) + 1] -
            sortOrder[(b.category_type || 0) + 1]
        )
        .map((group) => ({
          ...group,
          account_items: group.account_items?.filter(
            (item) =>
              //ノーマルモードのとき→現行と同様に「プライベートの支出」「プライベートの収入」を省き、「プライベート(account_item_id = -1)」を入れる
              //プロモードのとき→省かず、「プライベート(account_item_id = -1)」は入れない
              (editMode === 'pro' ||
                (item.name !== FILTER_ACCOUNT_ITEM.PRIVATE_INCOME &&
                  item.name !== FILTER_ACCOUNT_ITEM.PRIVATE_PAYMENT)) &&
              item.name.includes(inputText)
          )
        })),
    [inputText, reportCategories]
  )

  const flattenCategories = useMemo(
    () => filteredReportCategories.map((group) => group.account_items).flat(),
    [filteredReportCategories]
  )

  const totalItemCount = useMemo(
    () => flattenCategories.length,
    [flattenCategories]
  )

  const scrollToFocusedItem = (index: number) => {
    if (!menuRef.current) return
    const node = menuNodes.map.get(index)
    if (!node) return
    scrollIntoView(node, {
      scrollMode: 'if-needed',
      block: 'start',
      inline: 'start',
      boundary: menuRef.current
    })
  }

  useUpdateEffect(() => {
    setInputText('')
    const nextFocusIndex =
      flattenCategories.findIndex(
        (accountItem) => accountItem?.id === selectedAccountItemId
      ) || 0
    setFocusedIndex(nextFocusIndex)
    scrollToFocusedItem(nextFocusIndex)
  }, [isOpen])

  return (
    <Popover
      initialFocusRef={firstFouceRef}
      placement="bottom"
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setFocusedIndex(0)
      }}
      closeOnEsc={true}
    >
      <PopoverTrigger>
        <Box pos="relative" onClick={onOpen}>
          {popoverTrigger ?? (
            <>
              <Input
                readOnly
                color={selectedAccountItemId ? 'primary.black' : 'gray.400'}
                cursor="pointer"
                value={
                  selectedAccountItemId === null
                    ? '未選択'
                    : flattenCategories.find(
                        (accountItem) =>
                          accountItem?.id === selectedAccountItemId
                      )?.name || ''
                }
                w="180px"
                placeholder={props.placeholder || '未登録'}
                {...props}
              />
              <SelectTriangleIcon
                pos="absolute"
                right="8px"
                top="50%"
                transform="translateY(-50%)"
                w="10px"
                h="10px"
                fontSize="10px"
              />
            </>
          )}
        </Box>
      </PopoverTrigger>
      <Portal containerRef={containerRef}>
        <PopoverContent
          bgColor="white.100"
          borderRadius="8px"
          border="none"
          w="224px"
          maxH="300px"
          boxShadow="md"
          _focus={{
            boxShadow: 'none'
          }}
        >
          <Box borderTopRadius="8px">
            <Box
              borderTopRadius="8px"
              bgColor="primary.black"
              h="44px"
              py="15px"
              px="12px"
            >
              <Text
                color="white.100"
                fontSize="14px"
                fontWeight={700}
                lineHeight="100%"
              >
                カテゴリ
              </Text>
            </Box>
            <Input
              color="primary.black"
              _focus={{
                border: '0px',
                bgColor: 'white.100'
              }}
              border="none"
              placeholder="カテゴリ名で検索"
              ref={firstFouceRef}
              value={inputText}
              onChange={(e) => {
                setInputText(e.target.value)
                setFocusedIndex(0)
              }}
              onKeyDown={(e) => {
                e.stopPropagation()
                // IME変換時のEnterキーは無視する
                if (e.keyCode === 229) {
                  e.preventDefault()
                  return
                }
                switch (e.key) {
                  case 'ArrowDown': {
                    e.preventDefault()
                    e.stopPropagation()
                    if (focusedIndex + 1 < totalItemCount) {
                      setFocusedIndex((i) => i + 1)
                      scrollToFocusedItem(focusedIndex + 1)
                    }
                    break
                  }
                  case 'ArrowUp': {
                    e.preventDefault()
                    e.stopPropagation()

                    if (focusedIndex > 0) {
                      setFocusedIndex((i) => i - 1)
                      scrollToFocusedItem(focusedIndex - 1)
                    }
                    break
                  }
                  case 'Enter': {
                    if (totalItemCount <= 0) {
                      break
                    }
                    onChange(flattenCategories[focusedIndex] || null)
                    onClose()
                    e.stopPropagation()
                    break
                  }
                }
              }}
            />
          </Box>
          <Box borderBottomRadius="8px" overflowY="scroll" ref={menuRef}>
            {filteredReportCategories.map((group, i, arr) => {
              const { parentGroup, ...groupHeaderProps } = categoryAdaptor(
                group.category_type
              )
              const itemCountBeforeThisGroup = arr
                .slice(0, i)
                .reduce(
                  (prev, curr) => prev + (curr.account_items?.length || 0),
                  0
                )
              return (
                <React.Fragment key={i}>
                  {parentGroup && (
                    <AccountItemGroupHeader
                      {...groupHeaderProps}
                      bg={groupHeaderProps.borderColor}
                      color="white"
                    >
                      {parentGroup}
                    </AccountItemGroupHeader>
                  )}
                  {(group.account_items?.length || 0) > 0 &&
                    group.category_type !==
                      (-9999 as reportCategory.category_type) && (
                      <AccountItemGroupHeader {...groupHeaderProps}>
                        {groupHeaderProps.title}
                      </AccountItemGroupHeader>
                    )}
                  {group.account_items
                    ?.filter((accountItem) =>
                      accountItem.name.includes(inputText)
                    )
                    .map((accountItem, j) => {
                      return (
                        <AccountItemBox
                          key={j}
                          ref={menuNodes.ref(itemCountBeforeThisGroup + j)}
                          onClick={() => {
                            onChange(accountItem)
                            onClose()
                          }}
                          color="primary.black"
                          borderLeftColor={groupHeaderProps.borderColor}
                          bgColor={
                            focusedIndex === itemCountBeforeThisGroup + j
                              ? 'gray.200'
                              : 'white.100'
                          }
                        >
                          {accountItem.name}
                        </AccountItemBox>
                      )
                    })}
                </React.Fragment>
              )
            })}
          </Box>
        </PopoverContent>
      </Portal>
    </Popover>
  )
}

export const CategorySelectBox: React.FC<Props> = (props) => {
  const { sorted_report_category: reportCategories } = useReportCategories()

  return (
    <BaseCategorySelectBox
      {...props}
      reportCategories={reportCategories as unknown as reportCategory[]}
    />
  )
}

/**
 * /zo_admin以下で使うカテゴリ選択ボックス
 * */
export const AdmingCategorySelectBox: React.FC<Props & { userId: number }> = ({
  userId,
  ...props
}) => {
  const { sorted_report_category: reportCategories } =
    useAdminUltraReportCategories(userId)
  return (
    <BaseCategorySelectBox {...props} reportCategories={reportCategories} />
  )
}

/**
 * /tax_accountant以下で使うカテゴリ選択ボックス
 * */
export const TaxAccountantCategorySelectBox: React.FC<Props> = ({
  ...props
}) => {
  const { sorted_report_category: reportCategories } =
    usePartnerTaxAccountantCategories()
  return (
    <BaseCategorySelectBox {...props} reportCategories={reportCategories} />
  )
}

const categoryAdaptor = (
  type: -1 | reportCategory.category_type | undefined
) => {
  let settings: {
    parentGroup?: string
    title: string
    borderColor: string
    bg: string
    color: string
  } = {
    title: '',
    borderColor: 'transaction.incomeBlue.border',
    bg: 'white',
    color: 'transaction.incomeBlue.border'
  }
  switch (type) {
    case -1:
      settings = {
        title: '個人事業に関係ないお金',
        bg: 'black.56',
        borderColor: 'black.56',
        color: 'white'
      }
      break
    case 0:
      settings = {
        title: '資産',
        bg: 'transaction.paymentRed.light',
        borderColor: 'transaction.paymentRed.border',
        color: 'transaction.paymentRed.border'
      }
      break
    case 1:
      settings = {
        title: '負債',
        bg: 'transaction.incomeBlue.light',
        borderColor: 'transaction.incomeBlue.border',
        color: 'transaction.incomeBlue.border'
      }
      break
    case 2:
      settings = {
        title: '純資産',
        bg: 'transaction.incomeBlue.light',
        borderColor: 'transaction.incomeBlue.border',
        color: 'transaction.incomeBlue.border'
      }
      break
    case 3:
      settings = {
        parentGroup: '個人事業の収入',
        title: '収益',
        bg: 'transaction.incomeBlue.light',
        borderColor: 'transaction.incomeBlue.border',
        color: 'transaction.incomeBlue.border'
      }
      break
    case 4:
      settings = {
        parentGroup: '個人事業の支出',
        title: '費用',
        bg: 'transaction.paymentRed.light',
        borderColor: 'transaction.paymentRed.border',
        color: 'transaction.paymentRed.border'
      }
      break
    default:
      break
  }
  return settings
}
